import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useAppStore } from '@core/store'
import {
  useDetachedWindow,
  type DetachedWindowHandlers,
} from '@core/layout/janela-destacada/useDetachedWindow'
import PropertiesPanel from '@viewer/inspector/PropertiesPanel'

// Renders the right sidebar contents inside a real browser window
// (window.open + React createPortal). The new window inherits the
// page's JS context so the zustand store is shared; every selection
// change in the main viewer is reflected here without any IPC. Only
// the DOM and CSS need replication: we clone the document head's
// stylesheet nodes so Tailwind classes resolve identically.
function BrowserWindowSidebar() {
  const isWindow = useAppStore((s) => s.rightSidebarMode === 'window')
  const setMode = useAppStore((s) => s.setRightSidebarMode)
  // O mecanismo é o partilhado (`useDetachedWindow`): clonagem dos
  // estilos, tema seguido da janela principal, e a janela fecha-se com a
  // principal. Bloqueada ou fechada, volta ao painel fixo.
  const handlers = useMemo<DetachedWindowHandlers>(
    () => ({
      onBlocked: () => {
        setMode('fixed')
        // Sem aviso no ecrã: o modo 'window' fica guardado, e ao reabrir a
        // aplicação o browser bloqueia a janela (não houve gesto). Volta
        // ao painel fixo em silêncio, como antes.
        console.warn('[BrowserWindowSidebar] janela bloqueada')
      },
      onClose: () => setMode('fixed'),
    }),
    [setMode],
  )
  const { container } = useDetachedWindow(isWindow, handlers, {
    name: 'vn2r-properties',
    title: 'VN2R IFC Viewer · Propriedades',
    features: 'width=480,height=720,resizable=yes,scrollbars=yes',
  })

  if (!isWindow || !container) return null

  return createPortal(
    <>
      <div className="flex h-8 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs text-[var(--color-text)]">
        <span className="truncate">Propriedades · VN2R IFC Viewer</span>
        <button
          type="button"
          onClick={() => setMode('fixed')}
          title="Voltar a anexar à sidebar"
          className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          ↘ anexar
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto bg-[var(--color-bg)]">
        <div className="border-b border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-text)]">
          Propriedades
        </div>
        <PropertiesPanel />
      </div>
    </>,
    container,
  )
}

export default BrowserWindowSidebar
